import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Modal, Progress, Typography, Space, Alert } from 'antd';
import { LockOutlined, UnlockOutlined, CheckCircleOutlined, WarningOutlined, InfoCircleOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useI18n } from '../../hooks/useI18n';

const { Title, Text } = Typography;

const DashboardPassword = () => {
	const { t } = useI18n();
	const [form] = Form.useForm();
	const [isResetMode, setIsResetMode] = useState(false);
	const [isChangeMode, setIsChangeMode] = useState(false);
	const [resetModalVisible, setResetModalVisible] = useState(false);
	const [clearModalVisible, setClearModalVisible] = useState(false);
	const [clearPassword, setClearPassword] = useState('');
	const [passwordStrength, setPasswordStrength] = useState(0);
	const [passwordFeedback, setPasswordFeedback] = useState([]);

	useEffect(() => {
		const storedPassword = localStorage.getItem('dashboardPassword');
		if (storedPassword) {
			setIsResetMode(true);
		}
	}, []);

	const calculatePasswordStrength = (password) => {
		let strength = 0;
		let feedback = [];

		if (password.length >= 8) strength += 25;
		else feedback.push('At least 8 characters');

		if (/[A-Z]/.test(password)) strength += 25;
		else feedback.push('One uppercase letter');

		if (/[a-z]/.test(password)) strength += 25;
		else feedback.push('One lowercase letter');

		if (/[0-9]/.test(password)) strength += 25;
		else feedback.push('One number');

		setPasswordStrength(strength);
		setPasswordFeedback(feedback);
	};

	const handlePasswordChange = (e) => {
		const password = e.target.value;
		calculatePasswordStrength(password);
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			const storedPassword = localStorage.getItem('dashboardPassword');

			// If we're in reset mode (changing password), verify current password
			if (isResetMode && values.currentPassword !== undefined) {
				if (values.currentPassword !== storedPassword) {
					message.error('Current password is incorrect');
					return;
				}
			}

			if (values.newPassword !== values.confirmPassword) {
				message.error('New password and confirm password do not match');
				return;
			}

			if (passwordStrength < 50) {
				message.error('Password is too weak. Please choose a stronger password.');
				return;
			}

			localStorage.setItem('dashboardPassword', values.newPassword);
			message.success(isChangeMode ? t('dashboard_password.updated_successfully') : t('dashboard_password.set_successfully'));
			form.resetFields();
			setIsResetMode(true);
			setIsChangeMode(false);
			setPasswordStrength(0);
			setPasswordFeedback([]);
		} catch (error) {
			// Form validation error
		}
	};

	const handleResetPassword = () => {
		setResetModalVisible(true);
	};

	const handleClearPassword = () => {
		setClearModalVisible(true);
	};

	const confirmClear = () => {
		const storedPassword = localStorage.getItem('dashboardPassword');
		if (clearPassword !== storedPassword) {
			message.error(t('dashboard_password.incorrect_password'));
			return;
		}

		localStorage.removeItem('dashboardPassword');
		setIsResetMode(false);
		setIsChangeMode(false);
		setClearModalVisible(false);
		setClearPassword('');
		form.resetFields();
		setPasswordStrength(0);
		setPasswordFeedback([]);
		message.success(t('dashboard_password.clear_success'));
	};

	const confirmReset = () => {
		localStorage.removeItem('dashboardPassword');
		setIsResetMode(false);
		setIsChangeMode(false);
		setResetModalVisible(false);
		form.resetFields();
		setPasswordStrength(0);
		setPasswordFeedback([]);
		message.success(t('dashboard_password.reset_successfully'));
	};

	const getPasswordStrengthColor = () => {
		if (passwordStrength < 25) return '#ff4d4f';
		if (passwordStrength < 50) return '#faad14';
		if (passwordStrength < 75) return '#52c41a';
		return '#389e0d';
	};

	const getPasswordStrengthText = () => {
		if (passwordStrength < 25) return 'Very Weak';
		if (passwordStrength < 50) return 'Weak';
		if (passwordStrength < 75) return 'Good';
		return 'Strong';
	};

	return (
		<Card 
			bordered={false}
			id="dashboard-password"
			className="settings-card"
			title={
				<Space>
					<SafetyCertificateOutlined />
					{t('dashboard_password.title')}
				</Space>
			}
		>
			{isResetMode && !isChangeMode ? (
				// Password is already set - show status and reset option
				<div style={{ textAlign: 'center', padding: '20px 0' }}>
					<CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
					<Title level={4} style={{ marginBottom: '8px' }}>
						{t('dashboard_password.protection_active')}
					</Title>
					<Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
						{t('dashboard_password.protection_active_description')}
					</Text>
					<Space direction="vertical" style={{ width: '35%' }}>
						<Button
							type="primary"
							danger
							icon={<WarningOutlined />}
							onClick={handleResetPassword}
							block
							size="large">
							{t('dashboard_password.reset_protection')}
						</Button>
						<Button
							danger
							onClick={handleClearPassword}
							block
							size="large">
							{t('dashboard_password.clear_protection')}
						</Button>
						<Button
							type="link"
							onClick={() => setIsChangeMode(true)}
							block>
							{t('dashboard_password.change_password')}
						</Button>
					</Space>
				</div>
			) : (
				// Show form for setting new password or changing existing password
				<>
					{isChangeMode && (
						<Alert
							message={t('dashboard_password.change_password')}
							description={t('dashboard_password.change_password_description')}
							type="info"
							showIcon
							style={{ marginBottom: 20 }}
						/>
					)}
					{!isResetMode && !isChangeMode && (
						<Alert
							message={t('dashboard_password.set_protection')}
							description={t('dashboard_password.set_protection_description')}
							type="info"
							showIcon
							style={{ marginBottom: 20 }}
						/>
					)}

					<Form form={form} layout="vertical" onFinish={handleSubmit}>
						{isChangeMode && (
							<Form.Item
								label={t('dashboard_password.current_password')}
								name="currentPassword"
								rules={[{ required: true, message: t('dashboard_password.current_password_required') }]}
							>
								<Input.Password
									prefix={<LockOutlined />}
									placeholder={t('dashboard_password.current_password_placeholder')}
									size="large"
								/>
							</Form.Item>
						)}

						<Form.Item
							label={t('dashboard_password.new_password')}
							name="newPassword"
							rules={[
								{ required: true, message: t('dashboard_password.new_password_required') },
								{ min: 6, message: t('dashboard_password.password_min_length') }
							]}
						>
							<Input.Password
								prefix={<UnlockOutlined />}
								placeholder={t('dashboard_password.new_password_placeholder')}
								size="large"
								onChange={handlePasswordChange}
							/>
						</Form.Item>

						{passwordStrength > 0 && (
							<div style={{ marginBottom: 16 }}>
								<Text strong>{t('dashboard_password.password_strength')}: {getPasswordStrengthText()}</Text>
								<Progress
									percent={passwordStrength}
									strokeColor={getPasswordStrengthColor()}
									showInfo={false}
									size="small"
								/>
								{passwordFeedback.length > 0 && (
									<div style={{ marginTop: 8 }}>
										<Text type="warning" style={{ fontSize: '12px' }}>
											<WarningOutlined style={{ marginRight: 4 }} />
											Consider adding: {passwordFeedback.join(', ')}
										</Text>
									</div>
								)}
							</div>
						)}

						<Form.Item
							label={t('dashboard_password.confirm_password')}
							name="confirmPassword"
							rules={[
								{ required: true, message: t('dashboard_password.confirm_password_required') },
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue('newPassword') === value) {
											return Promise.resolve();
										}
										return Promise.reject(new Error(t('dashboard_password.passwords_not_match')));
									},
								}),
							]}
						>
							<Input.Password
								prefix={<UnlockOutlined />}
								placeholder={t('dashboard_password.confirm_password_placeholder')}
								size="large"
							/>
						</Form.Item>

						<Form.Item>
							<Space style={{ width: '100%', justifyContent: 'space-between' }}>
								{isChangeMode && (
									<Button onClick={() => { setIsChangeMode(false); form.resetFields(); setPasswordStrength(0); setPasswordFeedback([]); }}>
										{t('common.cancel')}
									</Button>
								)}
								<Button type="primary" htmlType="submit" size="large" style={{ flex: 1, marginLeft: isChangeMode ? 8 : 0 }}>
									{isChangeMode ? t('dashboard_password.update_password') : t('dashboard_password.set_protection')}
								</Button>
							</Space>
						</Form.Item>
					</Form>
				</>
			)}

			<div style={{ marginTop: 20, padding: 16, borderRadius: 4 }}>
				<Title level={5} style={{ marginBottom: 8 }}>
					<InfoCircleOutlined style={{ marginRight: 8 }} />
					{t('dashboard_password.about_protection')}
				</Title>
				<Text type="secondary">
					{t('dashboard_password.about_protection_description')}
				</Text>
			</div>

			<Modal
				title={
					<Space>
						<WarningOutlined style={{ color: '#faad14' }} />
						{t('dashboard_password.reset_protection')}
					</Space>
				}
				open={resetModalVisible}
				onOk={confirmReset}
				onCancel={() => setResetModalVisible(false)}
				okText={t('dashboard_password.reset_protection')}
				cancelText={t('common.cancel')}
				okButtonProps={{ danger: true }}
				centered>
				<Alert
					message={t('common.warning')}
					description={t('dashboard_password.reset_warning')}
					type="warning"
					showIcon
				/>
			</Modal>

			{/* Clear Password Confirmation Modal */}
			<Modal
				title={
					<Space>
						<WarningOutlined style={{ color: '#ff4d4f' }} />
						{t('dashboard_password.clear_protection_title')}
					</Space>
				}
				open={clearModalVisible}
				onOk={confirmClear}
				onCancel={() => {
					setClearModalVisible(false);
					setClearPassword('');
				}}
				okText={t('dashboard_password.clear_protection_confirm')}
				cancelText={t('common.cancel')}
				okButtonProps={{ danger: true }}
				centered>
				<div style={{ padding: '16px 0' }}>
					<p>{t('dashboard_password.clear_protection_question')}</p>
					<p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
						{t('dashboard_password.clear_protection_warning')}
					</p>
					<Input.Password
						prefix={<LockOutlined />}
						placeholder={t('dashboard_password.enter_current_password')}
						size="large"
						value={clearPassword}
						onChange={(e) => setClearPassword(e.target.value)}
						onPressEnter={confirmClear}
						style={{ marginBottom: '8px' }}
					/>
					<Text type="secondary" style={{ fontSize: '12px' }}>
						{t('dashboard_password.enter_password_to_confirm')}
					</Text>
				</div>
			</Modal>
		</Card>
	);
};

export default DashboardPassword;